import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, CalendarBlank, DownloadSimple, CaretLeft, CaretRight,
  Heartbeat, ClipboardText, Users, Notepad, Warning, CheckCircle,
  XCircle, ArrowClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtShort = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

export default function WeeklySummary() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Default: last 7 days
  const today = new Date();
  const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 6);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState(weekAgo.toISOString().split('T')[0]);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/weekly-summary?start_date=${startDate}&end_date=${endDate}`, { withCredentials: true });
      setData(res.data);
    } catch { toast.error('Failed to load summary'); }
    finally { setLoading(false); }
  }, [projectId, startDate, endDate]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const shiftWeek = (dir) => {
    const s = new Date(startDate + 'T00:00:00');
    const e = new Date(endDate + 'T00:00:00');
    s.setDate(s.getDate() + dir * 7);
    e.setDate(e.getDate() + dir * 7);
    setStartDate(s.toISOString().split('T')[0]);
    setEndDate(e.toISOString().split('T')[0]);
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/weekly-summary/pdf?start_date=${startDate}&end_date=${endDate}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Weekly_Summary_${startDate}_to_${endDate}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('PDF export failed'); }
    finally { setExporting(false); }
  };

  const healthColors = {
    green: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-700', dot: 'bg-green-500' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', dot: 'bg-amber-500' },
    red: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', dot: 'bg-red-500' },
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="animate-pulse text-zinc-500">Loading summary...</div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-zinc-50 text-zinc-500">No data available</div>;

  const { overview, dpr_summary, dmr_summary, site_log_highlights, action_summary, health } = data;
  const tc = data.time_control || {};
  const hc = healthColors[health.color] || healthColors.green;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="weekly-summary">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <Button onClick={() => navigate(-1)} variant="ghost" className="rounded-none h-7 text-xs px-2" data-testid="back-button">
              <ArrowLeft size={16} className="mr-1" /> Back
            </Button>
            <Button onClick={handleExportPdf} disabled={exporting} variant="outline"
              className="rounded-none h-7 text-xs px-3 border-zinc-300" data-testid="export-pdf-btn">
              <DownloadSimple size={14} className="mr-1" /> {exporting ? 'Exporting...' : 'Export PDF'}
            </Button>
          </div>
          <h1 className="text-base font-bold text-zinc-900">{overview.pi_number}</h1>
          <p className="text-xs text-zinc-500">{overview.client_name} — {overview.status}</p>

          {/* Week Picker */}
          <div className="flex items-center gap-2 mt-3 bg-zinc-50 border border-zinc-200 p-1.5">
            <button onClick={() => shiftWeek(-1)} className="p-1 hover:bg-zinc-200" data-testid="week-prev"><CaretLeft size={16} /></button>
            <div className="flex-1 flex items-center justify-center gap-2">
              <CalendarBlank size={14} className="text-zinc-400" />
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="text-xs bg-transparent border-none outline-none w-28 text-center" data-testid="start-date" />
              <span className="text-xs text-zinc-400">to</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="text-xs bg-transparent border-none outline-none w-28 text-center" data-testid="end-date" />
            </div>
            <button onClick={() => shiftWeek(1)} className="p-1 hover:bg-zinc-200" data-testid="week-next"><CaretRight size={16} /></button>
          </div>
          <p className="text-[10px] text-center text-zinc-400 mt-1">{fmtDate(startDate)} — {fmtDate(endDate)}</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-5 space-y-5">
        {/* Health Indicator */}
        <div className={`${hc.bg} ${hc.border} border-2 p-4 flex items-center gap-3`} data-testid="health-indicator">
          <div className={`w-4 h-4 rounded-full ${hc.dot} flex-shrink-0 ${health.color === 'amber' ? 'animate-pulse' : ''}`} />
          <div>
            <p className={`text-lg font-extrabold ${hc.text}`}>{health.status}</p>
            <p className="text-[10px] text-zinc-500">{health.signals} risk signal(s) detected</p>
          </div>
        </div>

        {/* Time Control */}
        {tc && tc.has_dates && (
          <div className="bg-white border border-zinc-200" data-testid="time-control-section">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
              <CalendarBlank size={14} className="text-zinc-500" />
              <h2 className="text-[10px] font-bold uppercase tracking-wider text-zinc-600">Project Time Control</h2>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
                <div className="text-center">
                  <p className="text-lg font-bold text-zinc-600">{tc.time_progress_pct}%</p>
                  <p className="text-[9px] text-zinc-500 font-semibold uppercase">Time Elapsed</p>
                </div>
                <div className="text-center">
                  <p className={`text-lg font-bold ${tc.status === 'on_track' ? 'text-green-700' : tc.status === 'delayed' ? 'text-red-700' : 'text-amber-700'}`}>{tc.work_progress_pct}%</p>
                  <p className="text-[9px] text-zinc-500 font-semibold uppercase">Work Done</p>
                </div>
                <div className="text-center">
                  <p className={`text-lg font-bold ${tc.deviation >= -5 ? 'text-green-700' : tc.deviation >= -15 ? 'text-amber-700' : 'text-red-700'}`}>{tc.deviation > 0 ? '+' : ''}{tc.deviation}%</p>
                  <p className="text-[9px] text-zinc-500 font-semibold uppercase">Deviation</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-zinc-700">{tc.days_remaining}</p>
                  <p className="text-[9px] text-zinc-500 font-semibold uppercase">Days Left</p>
                </div>
                <div className="text-center">
                  <p className={`text-sm font-extrabold ${tc.status === 'on_track' ? 'text-green-700' : tc.status === 'delayed' ? 'text-red-700' : tc.status === 'at_risk' ? 'text-amber-700' : 'text-zinc-500'}`}>{tc.status_label}</p>
                  <p className="text-[9px] text-zinc-500 font-semibold uppercase">Schedule</p>
                </div>
              </div>
              <div className="relative h-4 bg-zinc-200 overflow-hidden">
                <div className="absolute h-full bg-zinc-400 opacity-30" style={{ width: `${tc.time_progress_pct}%` }} />
                <div className={`absolute h-full ${tc.status === 'on_track' ? 'bg-green-500' : tc.status === 'delayed' ? 'bg-red-500' : 'bg-amber-500'}`} style={{ width: `${tc.work_progress_pct}%` }} />
                <div className="absolute top-0 h-full w-0.5 bg-zinc-800" style={{ left: `${tc.time_progress_pct}%` }} title={`Time: ${tc.time_progress_pct}%`} />
              </div>
              <div className="flex justify-between text-[9px] text-zinc-400 mt-1">
                <span>{tc.project_start_date}</span>
                <span>{tc.project_end_date}</span>
              </div>
            </div>
          </div>
        )}

        {/* KPI Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="kpi-strip">
          <KpiCard value={dpr_summary.total_entries} label="Work Entries" color="blue" />
          <KpiCard value={`${dpr_summary.days_reported}/${dpr_summary.total_workdays}`} label="Days Reported" color={dpr_summary.missing_days > 2 ? 'red' : 'blue'} />
          <KpiCard value={dmr_summary.total_manpower} label="Total Manpower" color="green" />
          <KpiCard value={action_summary.open_count} label="Open Tasks" color="zinc" />
          <KpiCard value={action_summary.overdue_count} label="Overdue" color={action_summary.overdue_count > 0 ? 'red' : 'green'} />
        </div>

        {/* DPR Section */}
        <Section icon={ClipboardText} title="Work Progress (DPR)" testId="dpr-section">
          {dpr_summary.trades.length === 0 ? (
            <p className="text-xs text-zinc-400 py-3">No work entries for this period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="dpr-table">
                <thead>
                  <tr className="bg-zinc-100 text-left">
                    <th className="px-3 py-2 text-[9px] font-bold uppercase text-zinc-500">Trade</th>
                    <th className="px-3 py-2 text-[9px] font-bold uppercase text-zinc-500 text-right">Qty Done</th>
                    <th className="px-3 py-2 text-[9px] font-bold uppercase text-zinc-500">Key Items</th>
                  </tr>
                </thead>
                <tbody>
                  {dpr_summary.trades.map((t, idx) => (
                    <tr key={idx} className="border-b border-zinc-100">
                      <td className="px-3 py-2 font-semibold text-zinc-800">{t.trade}</td>
                      <td className="px-3 py-2 text-right font-bold text-zinc-700">{t.total_qty.toLocaleString('en-IN', { maximumFractionDigits: 1 })}</td>
                      <td className="px-3 py-2 text-[10px] text-zinc-500">{t.items.slice(0, 3).map(i => `${i.description} (${i.qty} ${i.unit})`).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {dpr_summary.missing_days > 0 && (
            <div className="mt-2 bg-amber-50 border border-amber-200 p-2 text-[10px] text-amber-700 flex items-center gap-1.5" data-testid="missing-dpr-warning">
              <Warning size={12} /> {dpr_summary.missing_days} workday(s) had no DPR submitted
            </div>
          )}
        </Section>

        {/* DMR Section */}
        <Section icon={Users} title="Manpower — Planned vs Actual" testId="dmr-section">
          {dmr_summary.trades.length === 0 ? (
            <p className="text-xs text-zinc-400 py-3">No manpower data for this period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="dmr-table">
                <thead>
                  <tr className="bg-zinc-100 text-left">
                    <th className="px-3 py-2 text-[9px] font-bold uppercase text-zinc-500">Trade</th>
                    <th className="px-3 py-2 text-[9px] font-bold uppercase text-zinc-500 text-center">Planned</th>
                    <th className="px-3 py-2 text-[9px] font-bold uppercase text-zinc-500 text-center">Actual</th>
                    <th className="px-3 py-2 text-[9px] font-bold uppercase text-zinc-500 text-center">S/U/Sv</th>
                    <th className="px-3 py-2 text-[9px] font-bold uppercase text-zinc-500 text-center">Dev.</th>
                    <th className="px-3 py-2 text-[9px] font-bold uppercase text-zinc-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dmr_summary.trades.map((t, idx) => (
                    <tr key={idx} className={`border-b border-zinc-100 ${t.status === 'shortfall' ? 'bg-red-50' : t.status === 'excess' ? 'bg-amber-50' : ''}`}>
                      <td className="px-3 py-2 font-semibold text-zinc-800">{t.trade}</td>
                      <td className="px-3 py-2 text-center text-zinc-500">{t.planned}</td>
                      <td className="px-3 py-2 text-center font-bold text-zinc-700">{t.actual}</td>
                      <td className="px-3 py-2 text-center text-[10px] text-zinc-500">{t.skilled}/{t.unskilled}/{t.supervisor}</td>
                      <td className={`px-3 py-2 text-center font-bold ${t.deviation >= 0 ? 'text-green-700' : 'text-red-700'}`}>{t.deviation > 0 ? '+' : ''}{t.deviation}</td>
                      <td className="px-3 py-2">
                        <StatusBadge status={t.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center gap-4 mt-2 px-3 text-[10px] text-zinc-500 border-t border-zinc-200 pt-2">
                <span>Planned: <strong>{dmr_summary.total_planned}</strong></span>
                <span>Actual: <strong>{dmr_summary.total_manpower}</strong></span>
                <span className={`font-bold ${dmr_summary.total_deviation >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Deviation: {dmr_summary.total_deviation > 0 ? '+' : ''}{dmr_summary.total_deviation}
                </span>
              </div>
            </div>
          )}
        </Section>

        {/* Site Logs Section */}
        <Section icon={Notepad} title="Site Log Highlights" testId="logs-section">
          {site_log_highlights.length === 0 ? (
            <p className="text-xs text-zinc-400 py-3">No site logs for this period.</p>
          ) : (
            <div className="space-y-2">
              {site_log_highlights.map((l, idx) => (
                <div key={idx} className="border border-zinc-100 p-2.5 bg-zinc-50/50" data-testid={`log-entry-${idx}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[9px] font-bold text-zinc-400">{fmtShort(l.date)}</span>
                    <span className="text-[9px] font-mono text-zinc-300">{l.log_number}</span>
                  </div>
                  {l.notes && <p className="text-xs text-zinc-700"><span className="text-[9px] font-bold text-blue-600 mr-1">NOTES:</span>{l.notes}</p>}
                  {l.client_instructions && <p className="text-xs text-zinc-700 mt-0.5"><span className="text-[9px] font-bold text-pink-600 mr-1">CLIENT:</span>{l.client_instructions}</p>}
                  {l.issues && <p className="text-xs text-zinc-700 mt-0.5"><span className="text-[9px] font-bold text-red-600 mr-1">ISSUES:</span>{l.issues}</p>}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Action Center Section */}
        <Section icon={Warning} title="Action Center — Open Items" testId="actions-section">
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="text-center bg-zinc-50 border border-zinc-200 py-2">
              <p className="text-lg font-bold text-zinc-700">{action_summary.open_count}</p>
              <p className="text-[9px] text-zinc-500 font-semibold uppercase">Open</p>
            </div>
            <div className="text-center bg-red-50 border border-red-200 py-2">
              <p className="text-lg font-bold text-red-700">{action_summary.overdue_count}</p>
              <p className="text-[9px] text-red-600 font-semibold uppercase">Overdue</p>
            </div>
            <div className="text-center bg-amber-50 border border-amber-200 py-2">
              <p className="text-lg font-bold text-amber-700">{action_summary.critical_count}</p>
              <p className="text-[9px] text-amber-600 font-semibold uppercase">Critical</p>
            </div>
          </div>
          {action_summary.tasks.length > 0 && (
            <div className="space-y-1.5">
              {action_summary.tasks.map((t, idx) => (
                <div key={idx} className={`flex items-center justify-between p-2 border text-xs ${t.is_overdue ? 'bg-red-50 border-red-200' : 'bg-white border-zinc-200'}`} data-testid={`task-row-${idx}`}>
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-[9px] text-zinc-400 mr-1.5">{t.task_id}</span>
                    <span className="text-zinc-800">{t.title}</span>
                    {t.is_system_generated && <span className="ml-1 text-[8px] bg-indigo-100 text-indigo-700 px-1 py-0.5 font-bold">AUTO</span>}
                    {t.is_overdue && <span className="ml-1 text-[8px] bg-red-600 text-white px-1 py-0.5 font-bold">OVERDUE</span>}
                  </div>
                  <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 ${
                      t.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                      t.priority === 'High' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'
                    }`}>{t.priority}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Footer */}
        <div className="text-center text-[10px] text-zinc-400 pt-4 border-t border-zinc-200">
          Generated {data.generated_at} — {overview.company_name}
        </div>
      </main>
    </div>
  );
}

// --- Shared Components ---
function KpiCard({ value, label, color }) {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    zinc: 'bg-zinc-50 border-zinc-200 text-zinc-700',
  };
  return (
    <div className={`text-center border py-2 px-2 ${colors[color] || colors.zinc}`}>
      <p className="text-lg font-bold">{value}</p>
      <p className="text-[9px] font-semibold uppercase">{label}</p>
    </div>
  );
}

function Section({ icon: Icon, title, children, testId }) {
  return (
    <div className="bg-white border border-zinc-200" data-testid={testId}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
        <Icon size={14} className="text-zinc-500" />
        <h2 className="text-[10px] font-bold uppercase tracking-wider text-zinc-600">{title}</h2>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    shortfall: 'bg-red-100 text-red-700',
    excess: 'bg-amber-100 text-amber-700',
    on_track: 'bg-green-100 text-green-700',
  };
  const icons = {
    shortfall: Warning,
    excess: ArrowClockwise,
    on_track: CheckCircle,
  };
  const Icon = icons[status] || CheckCircle;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 ${styles[status] || 'bg-zinc-100 text-zinc-600'}`}>
      <Icon size={10} /> {status.replace('_', ' ')}
    </span>
  );
}
